import type * as React from 'react';
import moment from 'moment';
import { PopperProps } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { theme } from 'assets/style/them';

const DEFAULT_DATE_RANGE_FIELD_WIDTH = 188;

const blockManualDateEntry = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key.length === 1 || event.key === 'Backspace' || event.key === 'Delete') event.preventDefault();
};

interface DateRangeFieldProps {
    value: moment.Moment | null;
    onChange: (date: moment.Moment | null) => void;
    format: string;
    placeholder: string;
    showTime?: boolean;
    clearable?: boolean;
    disabled?: boolean;
    maxDate?: moment.Moment;
    minDate?: moment.Moment;
    minTime?: moment.Moment;
    maxTime?: moment.Moment;
    timeInterval?: number;
    popperPlacement?: PopperProps['placement'];
    inputWidth?: number;
    rangeStart?: moment.Moment | null;
    rangeEnd?: moment.Moment | null;
}

const DateRangeField = ({
    value,
    onChange,
    format,
    placeholder,
    showTime,
    clearable,
    disabled,
    maxDate,
    minDate,
    minTime,
    maxTime,
    timeInterval,
    popperPlacement,
    inputWidth,
    rangeStart,
    rangeEnd,
}: DateRangeFieldProps) => {
    const RangeDay = (props: PickersDayProps<moment.Moment>) => {
        const { day } = props;
        const isRangeStart = Boolean(rangeStart?.isSame(day, 'day'));
        const isRangeEnd = Boolean(rangeEnd?.isSame(day, 'day'));
        const isInRange = Boolean(rangeStart && rangeEnd && day.isBetween(rangeStart, rangeEnd, 'day', '[]'));

        return (
            <PickersDay
                {...props}
                data-date-range-day
                disableMargin={isInRange}
                selected={isRangeStart || isRangeEnd}
                sx={{
                    borderRadius: 1,
                    ...(isInRange && {
                        width: 40,
                        borderRadius: 0,
                        backgroundColor: theme.palette.primary.light,
                        '&:hover, &:focus': {
                            backgroundColor: theme.palette.primary.light,
                        },
                    }),
                    ...((isRangeStart || isRangeEnd) && {
                        borderRadius: 1,
                        color: theme.palette.primary.contrastText,
                        backgroundColor: theme.palette.primary.main,
                        '&:hover, &:focus': {
                            backgroundColor: theme.palette.primary.dark,
                        },
                    }),
                }}
            />
        );
    };

    const textFieldSx = {
        width: inputWidth ?? DEFAULT_DATE_RANGE_FIELD_WIDTH,
        '& .MuiInputBase-root': { fontSize: 14 },
        ...(clearable && {
            '& .clearButton': {
                width: 16,
                height: 16,
                padding: 0,
                marginRight: -0.5,
                color: theme.palette.common.white,
                backgroundColor: theme.palette.primary.main,
                '&:hover': { backgroundColor: theme.palette.primary.dark },
                '& .MuiSvgIcon-root': { fontSize: 12 },
            },
        }),
    };
    const slotProps = {
        field: clearable ? { clearable: true } : undefined,
        popper: {
            popperOptions: { strategy: 'fixed' as const },
            placement: popperPlacement ?? ('bottom-start' as const),
        },
        textField: {
            placeholder,
            size: 'small' as const,
            onKeyDown: blockManualDateEntry,
            sx: textFieldSx,
        },
    };

    return showTime ? (
        <DateTimePicker
            value={value}
            onChange={onChange}
            format={format}
            ampm={false}
            timeSteps={{ minutes: timeInterval ?? 1 }}
            maxDate={maxDate}
            minDate={minDate}
            minTime={minTime}
            maxTime={maxTime}
            disabled={disabled}
            slots={{ day: RangeDay }}
            slotProps={slotProps}
        />
    ) : (
        <DatePicker
            value={value}
            onChange={onChange}
            format={format}
            maxDate={maxDate}
            minDate={minDate}
            disabled={disabled}
            slots={{ day: RangeDay }}
            slotProps={slotProps}
        />
    );
};

export default DateRangeField;
